import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { services } from '../../context';
import { withDevPermissions } from '../../Services/devPermissions';

export const Permissions = {
  BitwerxOfferingPluginManagerView: 'BitwerxOfferingPluginManagerView',
  BitwerxOfferingPluginManagerEdit: 'BitwerxOfferingPluginManagerEdit',
  ModifyBitwerxOfferings: 'ModifyBitwerxOfferings',
  PluginManagerEdit: 'PluginManagerEdit',
  BulkManifestUpdate: 'BulkManifestUpdate',
  AdvancedConfiguration: 'AdvancedConfiguration',
} as const;

export type PermissionKey = (typeof Permissions)[keyof typeof Permissions];

export interface PermissionsContextValue {
  permissions: string[];
  hasPermission: (permissionOrList: string | string[]) => boolean;
  refetch: () => void;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

function checkHasPermission(
  permissionsToCheck: string | string[],
  userPermissions: string[] = [],
): boolean {
  const singleValueCheck = (value: string) => userPermissions?.indexOf(value) > -1;
  if (typeof permissionsToCheck === 'string') {
    return singleValueCheck(permissionsToCheck);
  }
  return Object.values(permissionsToCheck).every(singleValueCheck);
}

function readPermissionsFromAuth(): string[] {
  try {
    const account = services.auth.GetAccount();
    const claims = account?.idTokenClaims as { permissions?: string[] } | undefined;
    // Same development-only union the v1 auth slice applies. v2 reads the token directly rather
    // than through Redux, so without this a local run can exercise a new permission in v1 pages
    // and not in v2 ones. Returns the token's list untouched outside development.
    return withDevPermissions(claims?.permissions ?? []);
  } catch {
    // No resolvable account means nothing to add to — an unauthenticated principal must not pick
    // up the development overrides.
    return [];
  }
}

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>(() => readPermissionsFromAuth());

  const refetch = useCallback(() => {
    setPermissions(readPermissionsFromAuth());
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const hasPermission = useCallback(
    (permissionOrList: string | string[]) => checkHasPermission(permissionOrList, permissions),
    [permissions],
  );

  const value = useMemo<PermissionsContextValue>(
    () => ({ permissions, hasPermission, refetch }),
    [permissions, hasPermission, refetch],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export function usePermissionsContext(): PermissionsContextValue {
  const context = useContext(PermissionsContext);
  if (context === null) {
    throw new Error('usePermissionsContext must be used within a PermissionsProvider');
  }
  return context;
}

export default PermissionsContext;
